import { A } from "@ember/array"
import { set } from "@ember/object"
import type { PatchModel, PatchOps, PatchableDocument, PatchableRecord } from "@shared/models/patch"
import { batch } from "@supergrain/kernel"
import { cloneDeep, isEqual } from "lodash-es"
import { P, match } from "ts-pattern"

type Rewind = () => void

interface MutableEmberArray<T> {
  [index: number]: T

  addObject(value: T): T
  find(predicate: (value: T, index: number) => boolean): T | undefined
  findIndex(predicate: (value: T, index: number) => boolean): number
  insertAt(index: number, value: T): void
  popObject(): T | undefined
  pushObject(value: T): T
  removeAt(index: number, count?: number): this
  some(predicate: (value: T, index: number) => boolean): boolean
}

function isPatchableRecord(value: unknown): value is PatchableRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isPlainObject(value: unknown): value is PatchableRecord {
  if (typeof value !== "object" || value === null) return false

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

export function getValueAtPath(obj: unknown, path: string): unknown {
  if (obj == null || path === "") return undefined

  let current: unknown = obj
  for (const part of path.split(".")) {
    if (Array.isArray(current)) {
      current = current[Number(part)]
    } else if (isPatchableRecord(current)) {
      current = current[part]
    } else {
      return undefined
    }
  }

  return current
}

export function setValueAtPath(obj: PatchableRecord, path: string, value: unknown): void {
  if (!isPatchableRecord(obj) || path === "") return

  const parts = path.split(".")
  let current = obj
  const seen: string[] = []

  for (const part of parts.slice(0, -1)) {
    seen.push(part)
    const next = current[part]

    if (isPatchableRecord(next)) {
      current = next
    } else {
      const newRecord: PatchableRecord = {}
      set(obj, seen.join("."), newRecord)
      current = newRecord
    }
  }

  const key = parts[parts.length - 1]
  if (value === undefined) {
    set(obj, path, undefined)
    delete current[key]
  } else {
    set(obj, path, value)
  }
}

function ensurePath(obj: PatchableRecord, path: string, leafValue: unknown = {}): void {
  if (getValueAtPath(obj, path) !== undefined) return

  const parts = path.split(".")
  const seen: string[] = []

  for (const part of parts) {
    seen.push(part)
    const currentPath = seen.join(".")
    if (getValueAtPath(obj, currentPath) == null) {
      setValueAtPath(obj, currentPath, seen.length === parts.length ? cloneDeep(leafValue) : {})
    }
  }
}

function mutableEmberArray<T>(value: T[]): MutableEmberArray<T> {
  return A(value) as MutableEmberArray<T>
}

function arrayValueAtPath(obj: PatchableRecord, path: string): MutableEmberArray<unknown> {
  const value = getValueAtPath(obj, path)
  if (Array.isArray(value)) return mutableEmberArray(value)

  const array = mutableEmberArray<unknown>([])
  setValueAtPath(obj, path, array)
  return array
}

function opEntries(ops: PatchOps, op: keyof PatchOps): Array<[string, unknown]> {
  return Array.from(new Map(ops[op] ?? []))
}

function opValues(value: unknown): unknown[] {
  return match(value)
    .with(["$each", P.array(P._)], ([, values]) => values)
    .otherwise(() => [value])
}

function dottedPathPull(value: unknown): { path: string; expectedValue: unknown } | null {
  return match(value)
    .with(["$_dottedPath_", P.string, P._], ([, path, expectedValue]) => ({ path, expectedValue }))
    .otherwise(() => null)
}

function matchesPatchValue(actual: unknown, expected: unknown): boolean {
  return match([actual, expected])
    .with([P.when(isPlainObject), P.when(isPlainObject)], ([actualObject, expectedObject]) =>
      Object.entries(expectedObject).every(([key, value]) =>
        matchesPatchValue(actualObject[key], value)
      )
    )
    .otherwise(([actualValue, expectedValue]) => isEqual(actualValue, expectedValue))
}

function rememberPath(rewinds: Rewind[], obj: PatchableRecord, path: string): void {
  const previousValue = getValueAtPath(obj, path)
  rewinds.push(() => setValueAtPath(obj, path, previousValue))
}

export function applyOps(model: PatchableRecord, ops: PatchOps | undefined): Rewind {
  if (!ops) return () => {}

  const rewinds: Rewind[] = []

  for (const [path, value] of opEntries(ops, "set")) {
    rememberPath(rewinds, model, path)
    ensurePath(model, path)
    setValueAtPath(model, path, cloneDeep(value))
  }

  for (const [path] of opEntries(ops, "unset")) {
    rememberPath(rewinds, model, path)
    setValueAtPath(model, path, undefined)
  }

  for (const [path, value] of opEntries(ops, "inc")) {
    if (typeof value !== "number")
      throw new Error(`Invalid inc patch for ${path}: expected a number.`)

    const previousValue = getValueAtPath(model, path)
    rememberPath(rewinds, model, path)
    ensurePath(model, path, null)
    setValueAtPath(model, path, (typeof previousValue === "number" ? previousValue : 0) + value)
  }

  for (const [path, value] of opEntries(ops, "push")) {
    const array = arrayValueAtPath(model, path)
    for (const valueToAdd of opValues(value)) {
      rewinds.push(() => array.popObject())
      array.pushObject(cloneDeep(valueToAdd))
    }
  }

  for (const [path, value] of opEntries(ops, "pull")) {
    const array = arrayValueAtPath(model, path)
    const dottedPath = dottedPathPull(value)
    const index = array.findIndex((element) =>
      dottedPath
        ? getValueAtPath(element, dottedPath.path) === dottedPath.expectedValue
        : matchesPatchValue(element, value)
    )

    if (index > -1) {
      const previousValue = array[index]
      rewinds.push(() => array.insertAt(index, previousValue))
      array.removeAt(index, 1)
    }
  }

  for (const [path, values] of opEntries(ops, "pullAll")) {
    if (!Array.isArray(values)) continue

    const array = arrayValueAtPath(model, path)
    for (const value of values) {
      const index = array.findIndex((element) => matchesPatchValue(element, value))
      if (index > -1) {
        const previousValue = array[index]
        rewinds.push(() => array.insertAt(index, previousValue))
        array.removeAt(index, 1)
      }
    }
  }

  for (const [path, value] of opEntries(ops, "addToSet")) {
    const array = arrayValueAtPath(model, path)
    for (const valueToAdd of opValues(value)) {
      if (!array.some((element) => isEqual(element, valueToAdd))) {
        rewinds.push(() => array.popObject())
        array.addObject(cloneDeep(valueToAdd))
      }
    }
  }

  rewinds.reverse()
  return () => rewinds.forEach((rewind) => rewind())
}

export function patchDocument(doc: PatchableDocument, patch: PatchModel): Rewind {
  const rewinds: Rewind[] = []

  if (patch.embeddedDocument?.path) {
    const array = arrayValueAtPath(doc, patch.embeddedDocument.path)
    let embeddedDoc = array.find((element) =>
      matchesPatchValue(element, patch.embeddedDocument?.elemMatch ?? {})
    )

    if (embeddedDoc == null) {
      embeddedDoc = cloneDeep(patch.embeddedDocument.elemMatch)
      rewinds.push(() => array.popObject())
      array.pushObject(embeddedDoc)
    }

    if (isPatchableRecord(embeddedDoc)) {
      rewinds.push(applyOps(embeddedDoc, patch.embeddedDocument.ops))
    }
  }

  rewinds.push(applyOps(doc, patch.document.ops))

  const previousInternalVersion = doc.internalVersion
  set(doc, "internalVersion", (doc.internalVersion || 0) + 1)
  rewinds.push(() => {
    set(doc, "internalVersion", previousInternalVersion)
  })

  rewinds.reverse()
  return () => rewinds.forEach((rewind) => rewind())
}

export function batchPatchDocument(doc: PatchableDocument, patch: PatchModel): Rewind {
  return batch(() => {
    const rewind = patchDocument(doc, patch)
    return () => batch(rewind)
  })
}
